<script setup>
  import { computed } from "vue";
  import { useChartPreview } from "../composables/useChartPreview";

  const props = defineProps({
    colors: { type: Array, required: true },
    width: { type: Number, required: true },
    height: { type: Number, required: true },
    padding: { type: Number, required: true },
    seriesCount: { type: Number, required: true },
    categoryCount: { type: Number, required: true },
  });

  const { chartWidth, chartHeight, displayColors } = useChartPreview(props);

  const stackedColGap = 8;

  const stackedColWidth = computed(() => {
    const calculated = (chartWidth.value - (props.categoryCount - 1) * stackedColGap) / props.categoryCount;
    const maxWidth = chartWidth.value * 0.2;
    return Math.min(calculated, maxWidth);
  });

  const stackedColOffsetX = computed(() => {
    const totalColsWidth =
      props.categoryCount * stackedColWidth.value + (props.categoryCount - 1) * stackedColGap;
    return (chartWidth.value - totalColsWidth) / 2;
  });

  const stackedSegments = computed(() => {
    const segments = [];
    let offset = 0;
    for (let i = 0; i < props.seriesCount; i++) {
      const baseWidth = 1 / props.seriesCount;
      const variance = (i % 2 === 0 ? 0.05 : -0.05) * (i < props.seriesCount - 1 ? 1 : 0);
      const width = baseWidth + variance;
      segments.push({ offset, width });
      offset += width;
    }
    const total = segments.reduce((sum, s) => sum + s.width, 0);
    segments.forEach((s) => (s.width = s.width / total));
    let cumOffset = 0;
    segments.forEach((s) => {
      s.offset = cumOffset;
      cumOffset += s.width;
    });
    return segments;
  });
</script>

<template>
  <g :transform="`translate(${stackedColOffsetX}, 0)`">
    <g
      v-for="(cat, catIdx) in categoryCount"
      :key="catIdx">
      <rect
        v-for="(segment, segIdx) in stackedSegments"
        :key="segIdx"
        :x="padding + catIdx * (stackedColWidth + stackedColGap)"
        :y="height - padding - (segment.offset + segment.width) * chartHeight"
        :width="stackedColWidth"
        :height="segment.width * chartHeight"
        :fill="displayColors[segIdx % displayColors.length]" />
    </g>
  </g>
</template>
